import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { reportHandledSync } from "@/lib/observability";

export const Route = createFileRoute("/forgot-password")({
  head: () => ({
    meta: [
      { title: "Reset Your Password | Warrior Volunteer" },
      {
        name: "description",
        content:
          "Send yourself a reset link to get back into the T1D Warriors volunteer workspace.",
      },
      { property: "og:title", content: "Reset Your Password | Warrior Volunteer" },
      {
        property: "og:description",
        content: "Send yourself a reset link for the T1D Warriors volunteer workspace.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const email = String(new FormData(event.currentTarget).get("email") ?? "").trim();
    setBusy(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setSent(true);
    } catch (error) {
      reportHandledSync(error, { feature: "forgot-password", action: "handleSubmit" });
      toast.error(error instanceof Error ? error.message : "Could not send the reset link.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app-light flex min-h-screen flex-col items-center justify-center bg-background px-5 py-16">
      <Link to="/" className="mb-8 inline-flex">
        <img src="/t1dw-logo.png" alt="T1D Warriors" className="h-10 w-auto" />
      </Link>

      <div className="panel w-full max-w-md p-8">
        <p className="t-eyebrow">Warrior Volunteer</p>
        <h1 className="t-h2 mt-3 text-ice">Reset your password</h1>

        {sent ? (
          <>
            <p className="mt-5 text-sm text-muted-foreground">
              If that email belongs to a volunteer account, a reset link is on its way. The link
              opens a page where you can set a new password. Check your spam folder if it does not
              land in a couple of minutes.
            </p>
            <Button
              type="button"
              className="mt-7 w-full uppercase"
              onClick={() => navigate({ to: "/auth" })}
            >
              Back to sign in
            </Button>
          </>
        ) : (
          <>
            <p className="mt-4 text-sm text-muted-foreground">
              Enter the email you use for Warrior Volunteer and we will send you a link to set a new
              password.
            </p>
            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" name="email" type="email" required autoComplete="email" />
              </div>
              <Button type="submit" disabled={busy} className="w-full uppercase">
                {busy ? "Sending..." : "Send reset link"}
              </Button>
            </form>
            <p className="mt-6 text-center text-sm text-muted-foreground">
              Remembered it?{" "}
              <Link to="/auth" className="font-semibold text-cyan">
                Sign in
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
